import { Close, Email, Send } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  removeSharedWallet,
  renewSharedWallet,
  shareWallet,
} from 'src/services/wallet';
import * as Yup from 'yup';

import { SharedTable } from './SharedTable';

interface ShareWalletModalProps {
  open: boolean;
  onClose: () => void;
  walletName?: string;
  walletId: string | number;
}

export function ShareWalletModal({
  open,
  onClose,
  walletName,
  walletId,
}: ShareWalletModalProps) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const queryClient = useQueryClient();

  const handleShareWallet = (email: string) => {
    shareWalletMutation(email);
  };

  const { mutate: shareWalletMutation, isLoading: isSharingWallet } =
    useMutation({
      mutationFn: (email: string) => shareWallet(Number(walletId), email),
      onSuccess: () => {
        queryClient.invalidateQueries(['shared-wallet-users', walletId]);
        toast.success('Compartilhamento realizado com sucesso.');
        formik.resetForm();
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error?.message ||
          'Erro ao compartilhar carteira';
        toast.error(errorMessage);
      },
    });

  const removeSharedWalletMutation = useMutation({
    mutationFn: (email: string) => removeSharedWallet(Number(walletId), email),
    onSuccess: () => {
      queryClient.invalidateQueries(['shared-wallet-users', walletId]);
      toast.success('Compartilhamento removido com sucesso.');
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        'Erro ao remover compartilhamento';
      toast.error(errorMessage);
    },
  });

  const renewSharedWalletMutation = useMutation({
    mutationFn: (email: string) => renewSharedWallet(Number(walletId), email),
    onSuccess: () => {
      queryClient.invalidateQueries(['shared-wallet-users', walletId]);
      toast.success('Carteira compartilhada renovada com sucesso.');
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        'Erro ao renovar compartilhamento';
      toast.error(errorMessage);
    },
  });

  const handleRemoveShare = (email: string) => {
    removeSharedWalletMutation.mutate(email);
  };

  const handleRenewShare = (email: string) => {
    renewSharedWalletMutation.mutate(email);
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Email inválido').required('Email é obrigatório'),
  });

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema,
    onSubmit: ({ email }, { resetForm }) => {
      handleShareWallet(email);
      resetForm();
      return;
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '540px',
        },
      }}>
      <DialogTitle>
        <Typography variant="h5" align="center" marginX={2}>
          Compartilhe Sua Carteira
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              marginX={isMobile ? 2 : 4}>
              Compartilhe sua carteira com outros usuários para que eles possam
              visualizar os fundos. <br />
              <strong>Atenção:</strong> O compartilhamento é temporário e expira
              ao final do mês vigente.
            </Typography>

            <Box display="flex" gap={2} alignItems="flex-start">
              <TextField
                name="email"
                id="email"
                fullWidth
                size="small"
                placeholder={
                  isMobile
                    ? 'Informe o email'
                    : 'Com quem deseja compartilhar a carteira?'
                }
                value={formik.values.email}
                type="email"
                required
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                onKeyDown={(event) => {
                  if (event?.key === 'Enter') {
                    formik.handleSubmit();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email
                        aria-label="email"
                        color="action"
                        fontSize="small"
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                    color: 'text.disabled',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root':
                    {
                      color: 'primary.main',
                    },
                }}
              />
              {!isMobile && (
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={formik.isSubmitting || !formik.isValid}
                  sx={{
                    pointerEvents: isSharingWallet ? 'none' : 'auto',
                  }}>
                  {isSharingWallet ? (
                    <CircularProgress size="1.4rem" color="primary" />
                  ) : (
                    'Compartilhar'
                  )}
                </Button>
              )}
              {isMobile && (
                <Button
                  variant="outlined"
                  onClick={() => formik.handleSubmit()}
                  disabled={formik.isSubmitting || !formik.isValid}
                  color="primary"
                  sx={{
                    minWidth: '50px',
                    pointerEvents: isSharingWallet ? 'none' : 'auto',
                  }}>
                  {isSharingWallet ? (
                    <CircularProgress size="1.4rem" color="primary" />
                  ) : (
                    <Send color="inherit" fontSize="small" />
                  )}
                </Button>
              )}
            </Box>

            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                flexDirection={{ xs: 'column', sm: 'row' }}>
                <Typography variant="subtitle1" fontWeight={600} align="center">
                  Carteira&nbsp;
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main">
                  {walletName}
                </Typography>
                <Typography variant="subtitle1" fontWeight={600} align="center">
                  &nbsp;compartilhada com
                </Typography>
              </Box>
              <SharedTable
                walletId={walletId}
                removeSharedWallet={handleRemoveShare}
                renewSharedWallet={handleRenewShare}
              />
            </Box>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
}
